// App shell: hash router + bottom tab bar.
//   #/              send (share link)
//   #/r/<secret>    receive from a link ("/p" suffix: password protected)
//   #/nearby        devices on the same network
//   #/notes, #/notes/<id>, #/notes/<id>/history, #/devices, #/link/<secret>   my space
//   #/chats, #/chats/new, #/chats/<id>, #/invite/<secret>                      conversations

import './styles.css';
import { SECRET } from './core/ids';
import { h } from './ui/dom';
import { sendView } from './ui/send';
import { receiveView } from './ui/receive';
import { nearbyView } from './ui/nearby';
import { notesView } from './ui/notes';
import { editorView } from './ui/editor';
import { historyView } from './ui/history';
import { devicesView } from './ui/devices';
import { linkView } from './ui/link';
import { chatsView, inviteGuestView, inviteHostView, newGroupView } from './ui/chats';
import { chatView } from './ui/chat';
import { getConversations } from './space/current';
import type { View } from './ui/view';

const main = document.getElementById('view')!;
let current: View | null = null;
let currentHash = '';

const TABS = [
  { label: 'Envoyer', hash: '#/', match: (r: string) => r === '#/' || r.startsWith('#/r/') },
  { label: 'Proximité', hash: '#/nearby', match: (r: string) => r === '#/nearby' },
  { label: 'Discussions', hash: '#/chats', match: (r: string) => /^#\/(chats|invite)/.test(r) },
  { label: 'Notes', hash: '#/notes', match: (r: string) => /^#\/(notes|devices|link)/.test(r) },
  { label: 'Événements', hash: null },
] as const;

function route(): void {
  const hash = location.hash || '#/';
  if (hash === currentHash) return;
  // Leaving a running transfer loses it: ask first, and restore the URL if the answer is no.
  if (current?.busy?.() && !confirm('Un transfert est en cours. Le quitter l’interrompra. Continuer ?')) {
    history.replaceState(null, '', currentHash);
    return;
  }
  current?.destroy();
  currentHash = hash;
  main.replaceChildren();
  const receive = /^#\/r\/([^/]+)(\/p)?$/.exec(hash);
  const note = /^#\/notes\/([0-9a-f]{32})(\/history)?$/.exec(hash);
  const link = /^#\/link\/([^/]+)$/.exec(hash);
  const chat = /^#\/chats\/([0-9a-f]{32})$/.exec(hash);
  const invite = /^#\/invite\/([^/]+)$/.exec(hash);
  if (receive && SECRET.test(receive[1])) current = receiveView(main, receive[1], !!receive[2]);
  else if (hash === '#/nearby') current = nearbyView(main);
  else if (hash === '#/notes') current = notesView(main);
  else if (note) current = note[2] ? historyView(main, note[1]) : editorView(main, note[1]);
  else if (hash === '#/devices') current = devicesView(main);
  else if (link && SECRET.test(link[1])) current = linkView(main, link[1]);
  else if (hash === '#/chats') current = chatsView(main);
  else if (hash === '#/chats/new') current = inviteHostView(main);
  else if (hash === '#/chats/group') current = newGroupView(main);
  else if (chat) current = chatView(main, chat[1]);
  else if (invite && SECRET.test(invite[1])) current = inviteGuestView(main, invite[1]);
  else {
    if (hash !== '#/') history.replaceState(null, '', '#/');
    currentHash = '#/';
    current = sendView(main);
  }
  renderTabs();
  window.scrollTo(0, 0);
}

function renderTabs(): void {
  const nav = document.getElementById('tabs')!;
  nav.replaceChildren(
    ...TABS.map((t) =>
      t.hash
        ? h('a', { href: t.hash, class: `tab${t.match(currentHash) ? ' active' : ''}`, text: t.label })
        : h('span', { class: 'tab disabled', title: 'bientôt', text: t.label }),
    ),
  );
}

addEventListener('hashchange', route);
route();
// Conversations sync in the background whatever the screen, as soon as the app opens.
void getConversations();

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  navigator.serviceWorker.register('/sw.js').catch(() => {});
}
